import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';

interface AuthContextType {
  user: User | null;
  signUp: (email: string, password: string, userData?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<any>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Handle email confirmation redirect
        if (event === 'SIGNED_IN' && session?.user) {
          // Check if this is an email confirmation (user just validated their email)
          const urlParams = new URLSearchParams(window.location.search);
          const isEmailConfirmation = urlParams.has('type') && urlParams.get('type') === 'signup';
          
          if (isEmailConfirmation) {
            // Check if user is a provider
            try {
              const { data: providerData, error } = await supabase
                .from('providers')
                .select('subscription_status, is_active')
                .eq('user_id', session.user.id)
                .maybeSingle();
              
              if (!error && providerData) {
                // This is a provider - redirect to appropriate page
                if (providerData.subscription_status === 'pending') {
                  // Need to subscribe - redirect to verification page
                  window.location.href = '/provider-verification';
                } else {
                  // Go to dashboard
                  window.location.href = '/provider-dashboard';
                }
              } else {
                // Not a provider or error - go to home
                window.location.href = '/';
              }
            } catch (error) {
              console.error('Error checking provider status:', error);
              window.location.href = '/';
            }
          }
        }
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, userData?: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    });
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const value: AuthContextType = {
    user,
    signUp,
    signIn,
    signOut,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};